import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppPageHeader from './AppPageHeader';
import { LoadingSkeleton } from './LoadingSkeleton';
import { fetchFeedbackFeedbacks, peekFeedbackFeedbacksCache, SHEET_VIEW_URL } from './feedbackSheetApi';
import './Feedback.css';

const EXCLUDED_CLASSES = new Set(['0', '27']);
const FEEDBACK_HEADER_TITLE = 'AFS Live Class Feedback';

const overviewActionClassName =
  'px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 flex items-center gap-2 text-sm';

const sheetLinkButtonClassName =
  'px-4 py-2 text-sm font-medium text-blue-700 bg-white border border-blue-200 rounded-md hover:bg-blue-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2';

function ViewDataSheetButton() {
  return (
    <button
      type="button"
      onClick={() => window.open(SHEET_VIEW_URL, '_blank', 'noopener,noreferrer')}
      className={sheetLinkButtonClassName}
    >
      View data sheet
    </button>
  );
}

const FeedbackPage = () => {
  const navigate = useNavigate();
  const initialCached = peekFeedbackFeedbacksCache();
  const [feedbacks, setFeedbacks] = useState(() =>
    initialCached !== undefined ? initialCached : []
  );
  const [loading, setLoading] = useState(() => initialCached === undefined);
  const [error, setError] = useState(null);

  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [minCountInput, setMinCountInput] = useState('300');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [expandedClassKey, setExpandedClassKey] = useState(null);
  const [expandedClassNameKey, setExpandedClassNameKey] = useState(null);
  const [isOverviewModalOpen, setIsOverviewModalOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchFeedbacks = async () => {
      const cached = peekFeedbackFeedbacksCache();
      if (cached !== undefined) {
        if (!isMounted) return;
        setFeedbacks(cached);
        setLoading(false);
        setError(null);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const mapped = await fetchFeedbackFeedbacks();
        if (!isMounted) return;
        setFeedbacks(mapped);
      } catch (err) {
        if (!isMounted) return;
        setError(err.message || 'Failed to load feedback data');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchFeedbacks();
    return () => {
      isMounted = false;
    };
  }, []);

  const eligibleFeedbacks = useMemo(() => {
    return feedbacks.filter(item => {
      if (!item) return false;
      const cls = item.class != null ? String(item.class).trim() : '';
      return !EXCLUDED_CLASSES.has(cls);
    });
  }, [feedbacks]);

  useEffect(() => {
    setExpandedClassKey(null);
    setExpandedClassNameKey(null);
  }, [selectedGroup]);

  useEffect(() => {
    try {
      window.localStorage.setItem('feedbackSelectedClass', String(selectedClass));
    } catch (err) {
      // Ignore storage errors in private/restricted browser contexts.
    }
  }, [selectedClass]);

  const classOptions = useMemo(() => {
    const set = new Set();
    eligibleFeedbacks.forEach(item => {
      if (item && item.class != null && item.class !== '') {
        set.add(String(item.class));
      }
    });
    return Array.from(set).sort((a, b) => Number(a) - Number(b));
  }, [eligibleFeedbacks]);

  const subjectOptions = useMemo(() => {
    const set = new Set();
    eligibleFeedbacks.forEach(item => {
      if (item && item.subject) {
        set.add(item.subject);
      }
    });
    return Array.from(set).sort();
  }, [eligibleFeedbacks]);

  const filteredFeedbacks = useMemo(() => {
    return eligibleFeedbacks.filter(item => {
      if (!item) return false;
      const classMatch =
        selectedClass === 'all' ||
        String(item.class) === String(selectedClass);
      const subjectMatch =
        selectedSubject === 'all' ||
        item.subject === selectedSubject;
      return classMatch && subjectMatch;
    });
  }, [eligibleFeedbacks, selectedClass, selectedSubject]);

  const minCount = useMemo(() => {
    const trimmed = String(minCountInput).trim();
    if (!trimmed) return 0;
    const n = Number(trimmed);
    if (Number.isNaN(n) || n < 0) return 0;
    return Math.floor(n);
  }, [minCountInput]);

  const { stats, excludedStats } = useMemo(() => {
    const map = new Map();
    const groupByClass = selectedClass !== 'all';
    const groupBySubject = selectedSubject !== 'all';

    filteredFeedbacks.forEach(item => {
      if (!item) return;
      const teacherName = item.teacher_name || 'Unknown';
      const cls = item.class != null ? String(item.class) : 'N/A';
      const subject = item.subject || 'N/A';

      const keyParts = [teacherName];
      if (groupByClass) keyParts.push(cls);
      if (groupBySubject) keyParts.push(subject);
      const key = keyParts.join('|');

      if (!map.has(key)) {
        map.set(key, {
          key,
          teacherName,
          class: groupByClass ? cls : null,
          subject: groupBySubject ? subject : null,
          totalRating: 0,
          count: 0,
          items: [],
          seenFeedbackKeys: new Set(),
        });
      }
      const entry = map.get(key);
      const rating = Number(item.rating) || 0;
      const phone = item.student_phone ? String(item.student_phone) : null;
      const uniqueClassKey = item.class != null ? String(item.class) : 'N/A';
      const uniqueClassNameKey =
        item.class_name != null && String(item.class_name).trim() !== ''
          ? String(item.class_name).trim()
          : 'N/A';
      const uniqueFeedbackKey = phone
        ? `${phone}::${uniqueClassKey}::${uniqueClassNameKey}`
        : null;

      if (uniqueFeedbackKey) {
        if (entry.seenFeedbackKeys.has(uniqueFeedbackKey)) {
          return;
        }
        entry.seenFeedbackKeys.add(uniqueFeedbackKey);
      }

      entry.totalRating += rating;
      entry.count += 1;
      entry.items.push(item);
    });

    const rows = Array.from(map.values()).map(({ seenFeedbackKeys, ...row }) => ({
      ...row,
      avgRating: row.count > 0 ? row.totalRating / row.count : 0,
    }));

    const main = [];
    const excluded = [];

    rows.forEach(row => {
      if (minCount > 0 && row.count < minCount) {
        excluded.push(row);
      } else {
        main.push(row);
      }
    });

    const sorter = (a, b) => {
      if (b.avgRating !== a.avgRating) {
        return b.avgRating - a.avgRating;
      }
      if (b.count !== a.count) {
        return b.count - a.count;
      }
      return a.teacherName.localeCompare(b.teacherName);
    };

    main.sort(sorter);
    excluded.sort(sorter);

    return { stats: main, excludedStats: excluded };
  }, [filteredFeedbacks, minCount, selectedClass, selectedSubject]);

  const overallSummary = useMemo(() => {
    const allRows = [...stats, ...excludedStats];

    if (!allRows.length) {
      return {
        totalFeedbacks: 0,
        uniqueTeachers: 0,
        avgRating: 0,
      };
    }

    const teacherSet = new Set();
    let totalCount = 0;
    let totalRating = 0;

    allRows.forEach(row => {
      if (!row) return;
      teacherSet.add(row.teacherName || 'Unknown');
      totalCount += Number(row.count) || 0;
      totalRating += Number(row.totalRating) || 0;
    });

    return {
      totalFeedbacks: totalCount,
      uniqueTeachers: teacherSet.size,
      avgRating: totalCount > 0 ? totalRating / totalCount : 0,
    };
  }, [stats, excludedStats]);

  const reviewOverview = useMemo(() => {
    const teacherMap = new Map();

    filteredFeedbacks.forEach(item => {
      if (!item) return;
      const teacherName = String(item.teacher_name || 'Unknown').trim() || 'Unknown';
      const phone = String(item.student_phone || '').trim();
      const cls = String(item.class || '').trim();
      const className = String(item.class_name || '').trim() || 'N/A';
      const rating = Number(item.rating) || 0;

      if (!cls) return;
      if (EXCLUDED_CLASSES.has(cls)) return;

      if (!teacherMap.has(teacherName)) {
        teacherMap.set(teacherName, {
          seenFeedbackKeys: new Set(),
          items: [],
        });
      }

      const teacherEntry = teacherMap.get(teacherName);
      const uniqueFeedbackKey = phone ? `${phone}::${cls}::${className}` : null;

      if (uniqueFeedbackKey) {
        if (teacherEntry.seenFeedbackKeys.has(uniqueFeedbackKey)) {
          return;
        }
        teacherEntry.seenFeedbackKeys.add(uniqueFeedbackKey);
      }

      teacherEntry.items.push({ class: cls, rating });
    });

    const dedupedFeedbacks = Array.from(teacherMap.values()).flatMap(entry => entry.items);
    const classMap = new Map();
    let totalFeedbacks = 0;
    let totalRating = 0;

    dedupedFeedbacks.forEach(item => {
      if (!classMap.has(item.class)) {
        classMap.set(item.class, { totalRating: 0, count: 0 });
      }
      const classEntry = classMap.get(item.class);
      classEntry.totalRating += Number(item.rating) || 0;
      classEntry.count += 1;
      totalFeedbacks += 1;
      totalRating += Number(item.rating) || 0;
    });

    const classAverages = Array.from(classMap.entries())
      .map(([cls, values]) => ({
        class: cls,
        count: values.count,
        avg: values.count > 0 ? values.totalRating / values.count : 0,
      }))
      .sort((a, b) => (Number(a.class) || 0) - (Number(b.class) || 0));

    return {
      classAverages,
      totalFeedbacks,
      overallAvg: totalFeedbacks > 0 ? totalRating / totalFeedbacks : 0,
    };
  }, [filteredFeedbacks]);

  const renderSelectedGroupModal = () => {
    if (!selectedGroup) return null;
    const classGroups = selectedGroup.items.reduce((acc, item) => {
      const cls = item.class != null && item.class !== '' ? String(item.class) : 'N/A';
      if (!acc.has(cls)) {
        acc.set(cls, []);
      }
      acc.get(cls).push(item);
      return acc;
    }, new Map());
    const orderedClassGroups = Array.from(classGroups.entries()).sort((a, b) => {
      if (a[0] === 'N/A') return 1;
      if (b[0] === 'N/A') return -1;
      return Number(a[0]) - Number(b[0]);
    });

    return (
      <div
        className="feedback-modal-overlay"
        onClick={() => setSelectedGroup(null)}
      >
        <div
          className="feedback-modal"
          onClick={e => {
            e.stopPropagation();
          }}
        >
          <div className="feedback-modal-header">
            <div>
              <h2>{selectedGroup.teacherName}</h2>
              <p className="modal-subtitle">
                {selectedGroup.class && `Class ${selectedGroup.class} · `}
                {selectedGroup.subject || 'All subjects'}
              </p>
            </div>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => setSelectedGroup(null)}
            >
              ✕
            </button>
          </div>

          <div className="modal-summary">
            <span>
              Total feedbacks:{' '}
              <strong>
                {selectedGroup.items.length.toLocaleString('en-US')}
              </strong>
            </span>
            <span>
              Avg rating:{' '}
              <strong>{selectedGroup.avgRating.toFixed(2)}/5.00</strong>
            </span>
          </div>

          <div className="modal-table-wrapper">
            {orderedClassGroups.map(([cls, items]) => {
              const classKey = String(cls);
              const isClassExpanded = expandedClassKey === classKey;
              const classAvg =
                items.reduce((sum, item) => sum + (Number(item.rating) || 0), 0) /
                (items.length || 1);
              const classNameGroups = items.reduce((acc, item) => {
                const className =
                  item.class_name != null && String(item.class_name).trim() !== ''
                    ? String(item.class_name).trim()
                    : 'N/A';
                if (!acc.has(className)) {
                  acc.set(className, []);
                }
                acc.get(className).push(item);
                return acc;
              }, new Map());
              const orderedClassNameGroups = Array.from(classNameGroups.entries()).sort(
                (a, b) => a[0].localeCompare(b[0])
              );

              return (
                <div key={cls} className="accordion-section">
                  <button
                    type="button"
                    className="accordion-trigger"
                    onClick={() => {
                      setExpandedClassNameKey(null);
                      setExpandedClassKey(prev => (prev === classKey ? null : classKey));
                    }}
                  >
                    <span className="accordion-title">
                      {cls === 'N/A' ? 'Class N/A' : `Class ${cls}`}
                    </span>
                    <span className="accordion-meta val-muted">
                      {items.length.toLocaleString('en-US')} feedbacks · Avg{' '}
                      {classAvg.toFixed(2)}/5.00
                    </span>
                    <span className="accordion-arrow">{isClassExpanded ? '▾' : '▸'}</span>
                  </button>
                  {isClassExpanded &&
                    orderedClassNameGroups.map(([className, classNameItems]) => {
                    const classNameKey = `${classKey}::${className}`;
                    const isClassNameExpanded = expandedClassNameKey === classNameKey;
                    const classNameAvg =
                      classNameItems.reduce(
                        (sum, item) => sum + (Number(item.rating) || 0),
                        0
                      ) / (classNameItems.length || 1);

                    return (
                      <div key={`${cls}-${className}`} className="accordion-subsection">
                        <button
                          type="button"
                          className="accordion-trigger accordion-trigger-sub"
                          onClick={() =>
                            setExpandedClassNameKey(prev =>
                              prev === classNameKey ? null : classNameKey
                            )
                          }
                        >
                          <span className="accordion-title">
                            {className === 'N/A'
                              ? 'Class Name N/A'
                              : `Class Name: ${className}`}
                          </span>
                          <span className="accordion-meta val-muted">
                            {classNameItems.length.toLocaleString('en-US')} feedbacks
                            {' '}· Avg {classNameAvg.toFixed(2)}/5.00
                          </span>
                          <span className="accordion-arrow">
                            {isClassNameExpanded ? '▾' : '▸'}
                          </span>
                        </button>
                        {isClassNameExpanded && (
                          <table className="modern-table">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Time</th>
                                <th>Student</th>
                                <th>Rating</th>
                                <th>Feedback</th>
                              </tr>
                            </thead>
                            <tbody>
                              {classNameItems.map((item, index) => (
                                <tr
                                  key={`${cls}-${className}-${item.time}-${item.student_phone}-${index}`}
                                >
                                  <td className="center val-bold">{index + 1}</td>
                                  <td className="val-muted">
                                    {item.time
                                      ? new Date(item.time).toLocaleString('en-BD', {
                                          dateStyle: 'medium',
                                          timeStyle: 'short',
                                        })
                                      : '-'}
                                  </td>
                                  <td>
                                    <div className="student-cell">
                                      <span className="val-bold">
                                        {item.student_name || 'Unknown'}
                                      </span>
                                      {item.student_phone && (
                                        <span className="val-muted small-text">
                                          {item.student_phone}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="center val-bold">{item.rating}</td>
                                  <td className="feedback-cell">{item.feedback}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderReviewOverviewModal = () => {
    if (!isOverviewModalOpen) return null;

    return (
      <div
        className="feedback-modal-overlay feedback-overview-overlay"
        onClick={() => setIsOverviewModalOpen(false)}
      >
        <div
          className="feedback-modal feedback-overview-modal"
          onClick={e => {
            e.stopPropagation();
          }}
        >
          <div className="feedback-modal-header">
            <div>
              <h2>⭐ Class Rating Overview</h2>
              <p className="modal-subtitle">
                Overall rating{' '}
                <strong>{reviewOverview.overallAvg.toFixed(2)}/5.00</strong> from{' '}
                <strong>{reviewOverview.totalFeedbacks.toLocaleString('en-US')}</strong>{' '}
                feedbacks.
              </p>
            </div>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => setIsOverviewModalOpen(false)}
            >
              ✕
            </button>
          </div>

          <div className="review-overview-table-wrapper">
            <table className="modern-table review-overview-table">
              <thead>
                <tr>
                  <th className="center">Class</th>
                  <th className="center">Feedbacks</th>
                  <th className="center">Avg rating</th>
                </tr>
              </thead>
              <tbody>
                {reviewOverview.classAverages.map(row => (
                  <tr key={row.class}>
                    <td className="center">Class {row.class}</td>
                    <td className="center">{row.count.toLocaleString('en-US')}</td>
                    <td className="center">{row.avg.toFixed(2)} / 5.00</td>
                  </tr>
                ))}
                <tr>
                  <td className="center val-bold">Overall</td>
                  <td className="center val-bold">
                    {reviewOverview.totalFeedbacks.toLocaleString('en-US')}
                  </td>
                  <td className="center val-bold">
                    {reviewOverview.overallAvg.toFixed(2)} / 5.00
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppPageHeader
          onBack={() => navigate('/')}
          title={FEEDBACK_HEADER_TITLE}
          rightSlot={<ViewDataSheetButton />}
        />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="summary-grid">
            <LoadingSkeleton type="card" count={3} />
          </div>
          <LoadingSkeleton type="table" />
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppPageHeader
          onBack={() => navigate('/')}
          title={FEEDBACK_HEADER_TITLE}
          rightSlot={<ViewDataSheetButton />}
        />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="error-container">
            <h2>⚠️ Could not load feedback</h2>
            <p>{error}</p>
            <div className="error-actions">
              <button
                type="button"
                onClick={() => {
                  setLoading(true);
                  setError(null);
                  fetchFeedbackFeedbacks({ bypassCache: true })
                    .then(rows => {
                      setFeedbacks(rows);
                    })
                    .catch(e => {
                      setError(e.message || 'Failed to load feedback data');
                    })
                    .finally(() => {
                      setLoading(false);
                    });
                }}
              >
                Retry
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppPageHeader
        onBack={() => navigate('/')}
        title={FEEDBACK_HEADER_TITLE}
        rightSlot={
          <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setIsOverviewModalOpen(true)}
              className={overviewActionClassName}
            >
              View overview
            </button>
            <ViewDataSheetButton />
          </div>
        }
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="feedback-controls">
        <div className="filter-group">
          <label>
            Class
            <select
              value={selectedClass}
              onChange={e => setSelectedClass(e.target.value)}
            >
              <option value="all">All</option>
              {classOptions.map(cls => (
                <option key={cls} value={cls}>
                  Class {cls}
                </option>
              ))}
            </select>
          </label>
          <label>
            Subject
            <select
              value={selectedSubject}
              onChange={e => setSelectedSubject(e.target.value)}
            >
              <option value="all">All</option>
              {subjectOptions.map(sub => (
                <option key={sub} value={sub}>
                  {sub}
                </option>
              ))}
            </select>
          </label>
          <label>
            Min feedback count
            <input
              type="number"
              min="0"
              placeholder="All"
              value={minCountInput}
              onChange={e => setMinCountInput(e.target.value)}
            />
          </label>
        </div>
        <div className="filter-summary">
          <span>
            Showing{' '}
            <strong>
              {overallSummary.totalFeedbacks.toLocaleString('en-US')}
            </strong>{' '}
            feedbacks from{' '}
            <strong>
              {overallSummary.uniqueTeachers.toLocaleString('en-US')}
            </strong>{' '}
            teachers
          </span>
          <span className="avg-badge">
            Avg rating:{' '}
            <strong>{overallSummary.avgRating.toFixed(2)}/5.00</strong>
          </span>
        </div>
      </div>

      <div className="feedback-data-container">
        <div className="table-header-box">
          <h2>Class & Subject Wise Teacher Ratings</h2>
          <span className="val-muted">
            Sorted from highest to lowest average rating
          </span>
        </div>
        <div className="table-responsive-wrapper">
          <table className="modern-table">
            <thead>
              <tr>
                <th className="center">Teacher</th>
                {selectedClass !== 'all' && <th>Class</th>}
                {selectedSubject !== 'all' && <th>Subject</th>}
                <th className="center">Feedback Count</th>
                <th className="center">Average Rating</th>
                <th className="center arrow-header"></th>
              </tr>
            </thead>
            <tbody>
              {stats.length === 0 && excludedStats.length === 0 ? (
                <tr>
                  <td
                    colSpan={
                      4 +
                      (selectedClass !== 'all' ? 1 : 0) +
                      (selectedSubject !== 'all' ? 1 : 0)
                    }
                    className="center val-muted"
                  >
                    No feedback data available for the selected filters.
                  </td>
                </tr>
              ) : (
                <>
                  {stats.map((row, idx) => (
                    <tr
                      key={`${row.teacherName}-${row.class}-${row.subject}-${idx}`}
                      className="clickable-row"
                      onClick={() => setSelectedGroup(row)}
                    >
                      <td className="val-bold center-text">{row.teacherName}</td>
                      {selectedClass !== 'all' && (
                        <td>
                          <span className={`badge badge-${row.class}`}>
                            Class {row.class}
                          </span>
                        </td>
                      )}
                      {selectedSubject !== 'all' && <td>{row.subject}</td>}
                      <td className="center val-bold">{row.count}</td>
                      <td className="center val-bold">
                        {row.avgRating.toFixed(2)} / 5.00
                      </td>
                       <td className="center row-arrow">➜</td>
                    </tr>
                  ))}

                  {minCount > 0 && excludedStats.length > 0 && (
                    <>
                      <tr>
                        <td
                          colSpan={
                            4 +
                            (selectedClass !== 'all' ? 1 : 0) +
                            (selectedSubject !== 'all' ? 1 : 0)
                          }
                          className="center val-muted"
                        >
                          Excluded (below min feedback count)
                        </td>
                      </tr>
                      {excludedStats.map((row, idx) => (
                        <tr
                          key={`${row.teacherName}-${row.class}-${row.subject}-excluded-${idx}`}
                          className="row-excluded clickable-row"
                          onClick={() => setSelectedGroup(row)}
                        >
                          <td className="val-bold center-text">
                            {row.teacherName}
                          </td>
                          {selectedClass !== 'all' && (
                            <td>
                              <span className={`badge badge-${row.class}`}>
                                Class {row.class}
                              </span>
                            </td>
                          )}
                          {selectedSubject !== 'all' && <td>{row.subject}</td>}
                          <td className="center val-bold">{row.count}</td>
                          <td className="center val-bold">
                            {row.avgRating.toFixed(2)} / 5.00
                          </td>
                          <td className="center row-arrow">➜</td>
                        </tr>
                      ))}
                    </>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {renderSelectedGroupModal()}
      {renderReviewOverviewModal()}
      </main>
    </div>
  );
};

export default FeedbackPage;
